import { JSX, useEffect, useMemo, useState } from "react";
import DataTable, { Column } from "../../components/DataTable";
import InfoCard from "../../components/InfoCard";
import PaginationBar from "../../components/PaginationBar";
import Tabs from "../../components/Tabs";
import Tag from "../../components/Tags";
import TemplatesList from "./TemplatesList";
import RecurringInvoice from "./RecurringInvoice";
import { useDispatch, useSelector } from "react-redux";
import { AppDispatch, RootState } from "../../store";
import {
  fetchInvoices,
  setPage,
  setSize,
} from "../../features/feesAndPayment/FeesAndPaymentSlice";
import { Invoice } from "../../types/feesAndPaymentType";
import feesAndPaymentApis, { InvoiceStats } from "../../api/feesAndPaymentApis";
import { formatDateTime } from "../../util/functions/UniversalFunctions";
import useConfirmToast from "../../util/hooks/useConfirmToast";
import { toast } from "sonner";
import MarkAsPaidModal from "../../components/modal/MarkAsPaidModal";
import CreateTemplate from "../../components/modal/CreateTemplate";
import { useNavigate } from "react-router-dom";

const tabOptions = ["All Invoices", "Templates", "Recurring Invoice"];

export default function FeesList({
  activeTab,
  onTabChange,
}: {
  activeTab: string;
  onTabChange: (tab: string) => void;
}) {
  const [selectedInvoiceId, setSelectedInvoiceId] = useState<number | null>(
    null,
  );
  const [viewTemplate, setViewTemplate] = useState<Invoice | null>(null);
  const navigate = useNavigate();

  const dispatch = useDispatch<AppDispatch>();
  const { invoices, page, size } = useSelector(
    (state: RootState) => state.fees,
  );

  const branchCode = useSelector(
    (state: RootState) => state.access.schoolBranch,
  );

  const { confirm, modal } = useConfirmToast();

  const [stats, setStats] = useState<InvoiceStats>({
    pending: 0,
    paid: 0,
    partial: 0,
    draft: 0,
    scheduled: 0,
  });

  useEffect(() => {
    dispatch(
      fetchInvoices({
        branchCode: branchCode.schoolBranchCode,
        schoolCode: branchCode.schoolCode,
        page,
        size,
      }),
    );
  }, [page, size, branchCode, dispatch]);

  useEffect(() => {
    feesAndPaymentApis
      .getInvoiceStats(branchCode.schoolBranchCode)
      .then((res) => res.data.status === 1 && setStats(res.data.data));
  }, []);

  const filteredInvoices = useMemo(() => {
    if (activeTab === "Templates") {
      return invoices.filter((i) => i.paymentStatus === "DRAFT");
    }
    if (activeTab === "Recurring Invoice") {
      return invoices.filter((i) => i.paymentStatus === "SCHEDULED");
    }
    return invoices;
  }, [activeTab, invoices]);

  const paginatedData = useMemo(() => {
    const start = page * size;
    return filteredInvoices.slice(start, start + size);
  }, [filteredInvoices, page, size]);

  const handleDelete = async (invoiceId: number) => {
    const confirmed = await confirm(
      "Are you sure you want to delete this invoice?",
    );

    if (!confirmed) return;

    try {
      await feesAndPaymentApis.deleteTemplate(invoiceId);
      toast.success("Invoice deleted successfully");
      dispatch(
        fetchInvoices({
          branchCode: branchCode.schoolBranchCode,
          schoolCode: branchCode.schoolCode,
          page,
          size,
        }),
      );
    } catch (error) {
      // error already handled in API (toast)
    }
  };

  const columns: Column<Invoice>[] = [
    {
      label: "SL NO.",
      accessor: "invoiceId",
      render: (_, index) => <p>{index + 1}</p>,
    },
    {
      label: "Invoice Number",
      accessor: "invoiceNumber",
      searchable: true,
      render: (item) => <p className="text-[13px]">{item.invoiceId}</p>,
    },
    {
      label: "Invoice Date",
      accessor: "date",
      searchable: true,
      searchType: "date",
      render: (item) => (
        <p className="text-[13px]">{formatDateTime(item.dueDate) || "N/A"}</p>
      ),
    },
    {
      label: "Status",
      accessor: "status",
      searchable: true,
      searchType: "select",
      searchOptions: ["Sent", "Pending", "Completed"],
      render: (item) => (
        <p className="text-warning-600 font-semibold capitalize">
          {item.paymentStatus}
        </p>
      ),
    },
    {
      label: "Amount Payable(₹)",
      accessor: "amountPayable",
      render: (item) => (
        <div className="text-neutral-400 text-sm font-bold">
          ₹ {item.amount}
        </div>
      ),
    },
    {
      label: "Amount Received(₹)",
      accessor: "amountReceive",
      render: (item) => (
        <div className="text-success-600 text-sm font-bold">
          ₹ {item.amountReceived ?? "0"}
        </div>
      ),
    },
    {
      label: "Balance",
      accessor: "balance",
      render: (item) => {
        const amount = Number(item.amount) || 0;
        const received = Number(item.amountReceived) || 0;
        return (
          <div className="text-error-600 font-bold">₹ {amount - received}</div>
        );
      },
    },
    {
      label: "Action",
      accessor: "action",
      render: (item) => (
        <div className="flex gap-2">
          <Tag
            onClick={() => navigate(`/invoices/${item.invoiceId}`)}
            text="view Details"
          />

          {item.paymentStatus === "PENDING" && (
            <Tag
              text="Paid"
              color="success"
              onClick={() => setSelectedInvoiceId(item.invoiceId)}
            />
          )}

          <Tag
            text="Delete"
            color="danger"
            onClick={() => handleDelete(item.invoiceId)}
          />
        </div>
      ),
    },
  ];

  const tabContent: Record<string, JSX.Element> = {
    "All Invoices": (
      <div className="bg-white px-6 py-5 rounded-lg overflow-x-auto">
        <DataTable columns={columns} data={paginatedData} />
      </div>
    ),
    Templates: (
      <TemplatesList
        data={paginatedData}
        onDelete={handleDelete}
        onView={(invoice) => setViewTemplate(invoice)}
      />
    ),
    "Recurring Invoice": <RecurringInvoice data={paginatedData} />,
  };

  return (
    <div className="grid gap-4">
      {activeTab === "All Invoices" && (
        <div className="grid lg:grid-cols-3 gap-6 py-[18px] px-[14px] bg-white rounded-lg">
          <InfoCard
            label="Pending"
            value={stats.pending.toString()}
            bgColor="bg-error-50"
            textColor="text-error-700"
          />
          <InfoCard
            label="Partially Paid"
            value={stats.partial.toString()}
            bgColor="bg-warning-50"
            textColor="text-warning-700"
          />
          <InfoCard label="Completed" value={stats.paid.toString()} />
        </div>
      )}

      <div className="flex justify-between items-center">
        <Tabs
          tabs={tabOptions}
          activeTab={activeTab}
          onTabChange={onTabChange}
        />

        <PaginationBar
          currentPage={page}
          pageSize={size}
          totalItems={filteredInvoices.length}
          onPageChange={(p) => dispatch(setPage(p))}
          onPageSizeChange={(s) => dispatch(setSize(s))}
        />
      </div>
      {modal}

      {tabContent[activeTab]}

      {selectedInvoiceId && (
        <MarkAsPaidModal
          invoiceId={selectedInvoiceId}
          onClose={() => setSelectedInvoiceId(null)}
          onSuccess={() =>
            dispatch(
              fetchInvoices({
                branchCode: branchCode.schoolBranchCode,
                schoolCode: branchCode.schoolCode,
                page,
                size,
              }),
            )
          }
        />
      )}

      {viewTemplate && (
        <CreateTemplate
          mode="VIEW"
          template={viewTemplate}
          onClose={() => setViewTemplate(null)}
        />
      )}
    </div>
  );
}
